import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef } from 'react';

function generateId(prefix) {
    return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ChartOverlay({ title, description, onClose, children }) {
    const panelRef = useRef(null);
    const closeButtonRef = useRef(null);
    const previousActiveElement = useRef(null);

    const titleId = useMemo(() => generateId('chart-overlay-title'), []);
    const descriptionId = useMemo(() => (description ? generateId('chart-overlay-desc') : undefined), [description]);

    useEffect(() => {
        previousActiveElement.current = document.activeElement;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
            previousActiveElement.current?.focus?.();
        };
    }, [onClose]);

    useEffect(() => {
        closeButtonRef.current?.focus?.();
    }, []);

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose?.();
        }
    };

    return createPortal(
        <div className="chart-overlay" onClick={handleBackdropClick}>
            <div
                className="chart-overlay__panel"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                ref={panelRef}
            >
                <header className="chart-overlay__header">
                    <div className="chart-overlay__header-text">
                        <h3 id={titleId}>{title}</h3>
                        {description ? (
                            <p id={descriptionId}>{description}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className="chart-overlay__close"
                        onClick={onClose}
                        aria-label="Close expanded chart"
                        ref={closeButtonRef}
                    >
                        ✕
                    </button>
                </header>
                <div className="chart-overlay__body">{children}</div>
            </div>
        </div>,
        document.body
    );
}

export default ChartOverlay;
